import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, put, del } from "@/services/ApiService";
import {
  GET_COUPONS,
  CREATE_COUPON,
  GET_COUPON_BY_ID,
  UPDATE_COUPON,
  DELETE_COUPON,
  APPLY_COUPON,
} from "@/services/ApiRoutes";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Coupon {
  _id: string;
  code: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  currency?: string;
  minOrderAmount?: number;
  maxUses?: number;
  usedCount?: number;
  expiresAt?: string;
  isActive: boolean;
  description?: string;
  createdAt: string;
}

export interface AppliedCoupon {
  code: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  currency?: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
}

interface CouponState {
  coupons: Coupon[];
  currentCoupon: Coupon | null;
  appliedCoupon: AppliedCoupon | null;
  loading: boolean;
  applyLoading: boolean;
  error: string | null;
  applyError: string | null;
  total: number;
}

const initialState: CouponState = {
  coupons: [],
  currentCoupon: null,
  appliedCoupon: null,
  loading: false,
  applyLoading: false,
  error: null,
  applyError: null,
  total: 0,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchCoupons = createAsyncThunk(
  "coupons/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await get(GET_COUPONS);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch coupons");
    }
  }
);

export const fetchCouponById = createAsyncThunk(
  "coupons/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await get(GET_COUPON_BY_ID(id));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch coupon");
    }
  }
);

export const createCouponAction = createAsyncThunk(
  "coupons/create",
  async (
    data: Partial<Coupon>,
    { rejectWithValue }
  ) => {
    try {
      const res = await post(CREATE_COUPON, data);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to create coupon");
    }
  }
);

export const updateCouponAction = createAsyncThunk(
  "coupons/update",
  async (
    { id, data }: { id: string; data: Partial<Coupon> },
    { rejectWithValue }
  ) => {
    try {
      const res = await put(UPDATE_COUPON(id), data);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to update coupon");
    }
  }
);

export const deleteCouponAction = createAsyncThunk(
  "coupons/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await del(DELETE_COUPON(id));
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete coupon");
    }
  }
);

export const applyCouponAction = createAsyncThunk(
  "coupons/apply",
  async (
    { code, orderAmount }: { code: string; orderAmount: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await post(APPLY_COUPON, { code, orderAmount });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to apply coupon"
      );
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const couponSlice = createSlice({
  name: "coupons",
  initialState,
  reducers: {
    clearAppliedCoupon: (state) => {
      state.appliedCoupon = null;
      state.applyError = null;
    },
    clearCouponError: (state) => {
      state.error = null;
      state.applyError = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCoupons
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.coupons = Array.isArray(payload) ? payload : payload?.coupons || payload?.data || [];
        state.total = payload?.total || state.coupons.length;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchCouponById
    builder
      .addCase(fetchCouponById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCouponById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCoupon = action.payload;
      })
      .addCase(fetchCouponById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // createCoupon
    builder
      .addCase(createCouponAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCouponAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.coupons.unshift(action.payload);
      })
      .addCase(createCouponAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // updateCoupon
    builder
      .addCase(updateCouponAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCouponAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const idx = state.coupons.findIndex((c) => c._id === action.payload._id);
          if (idx !== -1) state.coupons[idx] = action.payload;
          state.currentCoupon = action.payload;
        }
      })
      .addCase(updateCouponAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteCoupon
    builder
      .addCase(deleteCouponAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCouponAction.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = state.coupons.filter((c) => c._id !== action.payload);
      })
      .addCase(deleteCouponAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // applyCoupon
    builder
      .addCase(applyCouponAction.pending, (state) => {
        state.applyLoading = true;
        state.applyError = null;
        state.appliedCoupon = null;
      })
      .addCase(applyCouponAction.fulfilled, (state, action) => {
        state.applyLoading = false;
        state.appliedCoupon = action.payload;
      })
      .addCase(applyCouponAction.rejected, (state, action) => {
        state.applyLoading = false;
        state.applyError = action.payload as string;
      });
  },
});

export const { clearAppliedCoupon, clearCouponError } = couponSlice.actions;
export default couponSlice.reducer;
